<template>
  <div class="book-reading-nav">
    <div class="book-reading-nav__container">
      <router-link
        v-if="doc.prevLink"
        replace
        :to="doc.prevLink.route"
        class="book-reading-nav__prev-box"
      >
        <div class="book-reading-nav__link">
          <span class="book-reading-nav__icon iconfont iprev"></span>Previous
        </div>
        <p class="book-reading-nav__name book-reading-nav__prev-name">
          {{ doc.prevLink.title }}
        </p>
      </router-link>
      <router-link
        v-else
        replace
        :to="doc.previewLink"
        class="book-reading-nav__prev-box"
      >
        <div class="book-reading-nav__link">
          <span class="book-reading-nav__icon iconfont iprev"></span>Back
        </div>
        <p class="book-reading-nav__name book-reading-nav__prev-name">
          Back to Preview page
        </p>
      </router-link>
    </div>

    <div class="book-reading-nav__container">
      <router-link
        v-if="doc.nextLink"
        replace
        :to="doc.nextLink.route"
        class="book-reading-nav__next-box"
        @click.capture="onNextClick"
      >
        <div class="book-reading-nav__link book-reading-nav__next">
          Next<span class="book-reading-nav__icon iconfont inext"></span>
        </div>
        <p class="book-reading-nav__name book-reading-nav__next-name">
          {{ doc.nextLink.title }}
        </p>
      </router-link>
      <div
        v-else-if="doc.doneStyle === 'button'"
        class="book-reading-nav__next-box"
      >
        <div class="book-reading-nav__button-wrapper">
          <AppButton
            class="book-reading-nav__btn btns btns--yellow"
            @click="doc.doneClick()"
          >
            Mark as Finished
          </AppButton>
          <p class="book-reading-nav__name book-reading-nav__next-name">
            {{ doc.doneText }}
          </p>
        </div>
      </div>
      <a v-else class="book-reading-nav__next-box" @click="doc.doneClick()">
        <div class="book-reading-nav__link book-reading-nav__next">
          Done<span class="book-reading-nav__icon iconfont inext"></span>
        </div>
        <p class="book-reading-nav__name book-reading-nav__next-name">
          {{ doc.doneText }}
        </p>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DocView } from '@/models/doc'
import { saveProgress } from '@/services/readingProgress'
import { Sentry } from '@/services/sentry'
import { completionService } from '@/services/completion'

import AppButton from '@/app/components/button/AppButton.vue'

const props = defineProps<{
  doc: DocView
}>()

/**
 * Mark the current chapter as completed when "Next" is clicked.

 * We use `@click.capture`, making this run before the navigation change so
 * that we complete the chapter that is being navigated away from.
 */
async function onNextClick(): Promise<void> {
  const doc = props.doc
  if (!doc.isCurrentPageChapter || doc.isCompletedPage(doc.currentPage)) {
    return
  }

  try {
    const completed = await saveProgress(doc, doc.currentPage)
    if (!completed || doc.doc_type !== 'book') {
      return
    }

    // Enroll the user in the guide completion experiment, if needed, and
    // register the popup for treatment users.
    await completionService.registerCompletion(doc, 'passive')
  } catch (error) {
    // Navigation doesn't wait for this handler, so report failures here.
    Sentry.captureException(error)
  }
}
</script>
